import { JHA_THEMES, setJhaWcTheme } from '@banno/jha-wc/src/styles/theme-provider';
import { DiContainer, InjectionToken } from '@jack-henry/frontend-utils/di';
import { JhHttpClientCached } from '@jack-henry/frontend-utils/http';
import { DynamicComponentRegistry } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { AlarmClock, parseTime } from '@treasury/alarm-clock';
import { AnalyticsEvent, AnalyticsService, NullAnalyticsService } from '@treasury/core/analytics';
import { CoBrowseService } from '@treasury/core/co-browse';
import { ConfigurationService } from '@treasury/core/config';
import { TmHttpClient } from '@treasury/core/http';
import { LoggingService } from '@treasury/core/logging';
import { NavigationService } from '@treasury/core/navigation';
import { AchService } from '@treasury/domain/ach';
import { AccountService, ChannelAuthenticationService } from '@treasury/domain/channel/services';
import { DashboardService } from '@treasury/domain/dashboard';
import { EntitlementsService } from '@treasury/domain/entitlements';
import { NavigationsService } from '@treasury/domain/navigation';
import { RemoteDepositCaptureService } from '@treasury/domain/remote-deposit-capture';
import { AuthFlowEvent, AuthenticationService } from '@treasury/domain/services/authentication';
import { DeviceService } from '@treasury/domain/services/device/device.service';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import {
    FeatureVisibilityService,
    PwaVisibilityFeatureConditionMap,
} from '@treasury/domain/services/feature-visibility';
import {
    LogoutManager,
    OpenIdleDialogToken,
    OpenTimeAccessDialogToken,
} from '@treasury/domain/services/logout';
import { StatusService } from '@treasury/domain/services/status';
import { OpenIdentityDialogToken, TmHttpClientMfa } from '@treasury/domain/shared';
import '@treasury/omega/css/index.css';
import { openIdentityDialog } from '@treasury/omega/domain-components/identity-verification-dialog';
import { openIdleDialog } from '@treasury/omega/domain-components/idle-logout-dialog';
import { openTimeAccessDialog } from '@treasury/omega/domain-components/time-access-logout-dialog';
import { OmegaDialogService } from '@treasury/omega/services/omega-dialog';
import { TmFooterService } from '@treasury/presentation';
import { OfflineDialogService } from '@treasury/presentation/components/offline';
import { ThemeService } from '@treasury/presentation/services/theme';
import colors from '@treasury/presentation/styles/colors.css';
import baseStyles from '@treasury/presentation/styles/styles.css';
import { SessionStorageService, WindowService } from '@treasury/utils/services';
import camelCase from 'camelcase';
import { AppComponent } from './components/app-component';
import { importTemplates, registerWorker } from './helpers';
import { ApplicationAreaService } from './services/application-area';
import { PwaNavigationService } from './services/navigation';

const [, , institutionId] = window.location.pathname.split('/');

document.adoptedStyleSheets = [...document.adoptedStyleSheets, baseStyles, colors];

const themeColorMetaTag = document.createElement('meta');
themeColorMetaTag.name = 'theme-color';
themeColorMetaTag.content = getComputedStyle(document.documentElement).getPropertyValue(
    '--brand-primary'
);
document.getElementsByTagName('head')[0].appendChild(themeColorMetaTag);

(async function main(institutionId: string, version: string) {
    setJhaWcTheme(JHA_THEMES.DESIGN_SYSTEM);
    const worker = await registerWorker(institutionId, version);
    await bootstrap(worker);
    await importTemplates();
})(institutionId, window.treasury.version);

function configureBranding(institutionId: string) {
    const head = document.querySelector('HEAD');
    const link = document.createElement('link');

    if (!head) {
        throw new Error('Could not configure branding. No head or link element.');
    }

    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = `/branding/${institutionId}/style.css`;
    head.appendChild(link);
    const faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.href = `/branding/${institutionId}/favicon.ico`;
    head.appendChild(faviconLink);
}

async function startAlarmClock(achService: AchService) {
    const fiClock = AlarmClock.getInstance();
    achService.getCutoffTimes().then(cutoffs => {
        fiClock.time = cutoffs.currentFiTime;
        fiClock.timeZone =
            /(All (\w+))/.exec(cutoffs.timeZone || '')?.[2] || cutoffs.timeZone || '';
        cutoffs.processingCutoffs?.forEach(cutoff => {
            const { startTime, cutoffTime, productType } = cutoff;
            if (startTime) {
                const startKeyName = camelCase(`${productType} start`);
                fiClock.setAlarm(startKeyName, startTime);
            }
            if (cutoffTime) {
                const cutoffKeyName = camelCase(`${productType} cutoff`);
                fiClock.setAlarm(cutoffKeyName, cutoffTime);
                const cutoffTimeObject = parseTime(cutoffTime);
                cutoffTimeObject.subtractMinutes(30);
                fiClock.setAlarm(`${cutoffKeyName}-30Minutes`, cutoffTimeObject);
            }
        });
    });
}

async function bootstrap(worker?: ServiceWorkerRegistration) {
    const di = new DiContainer();
    di.provide(DynamicComponentRegistry, new DynamicComponentRegistry(true));
    di.provide(OpenIdentityDialogToken as unknown as InjectionToken<unknown>, openIdentityDialog);
    di.provide(OpenIdleDialogToken as unknown as InjectionToken<unknown>, openIdleDialog);
    di.provide(
        OpenTimeAccessDialogToken as unknown as InjectionToken<unknown>,
        openTimeAccessDialog
    );
    di.provide(TmHttpClient, TmHttpClientMfa);
    di.provide(NavigationService, PwaNavigationService);
    di.provideFactoryAsync(
        FeatureFlagService,
        [
            TmHttpClient,
            SessionStorageService,
            ConfigurationService,
            AuthenticationService,
            AccountService,
            LoggingService,
        ] as const,

        async (http, storage, config, auth, accountService, logger) => {
            await auth.authenticated$.toPromise();

            const { loginId, isAdmin, isSuperUser, companyId } =
                await accountService.getCurrentUser();
            const { institutionId } = config;
            const ffService = new FeatureFlagService(http, storage, config, logger);

            await ffService.init(institutionId, {
                userId: loginId,
                companyId,
                isAdmin,
                isSuperUser,
            });

            return ffService;
        }
    );
    di.provideFactoryAsync(
        FeatureVisibilityService,
        [
            FeatureFlagService,
            EntitlementsService,
            AuthenticationService,
            NavigationsService,
            DashboardService,
            RemoteDepositCaptureService,
            DeviceService,
        ] as const,
        async (ffService, entitlements, auth, navigations, dashboard, rdc, deviceService) => {
            await auth.authenticated$.toPromise();
            const fvService = new FeatureVisibilityService(
                ffService,
                entitlements,
                auth,
                navigations,
                dashboard,
                rdc,
                deviceService
            );

            await fvService.init(PwaVisibilityFeatureConditionMap);
            return fvService;
        }
    );

    di.provideFactory(LoggingService, [WindowService, ConfigurationService], (window, config) => {
        const logger = new LoggingService(config, window);
        logger.init();

        return logger;
    });

    di.provideFactoryAsync(
        AnalyticsService,
        [
            ConfigurationService,
            FeatureFlagService,
            AuthenticationService,
            AccountService,
            LoggingService,
        ] as const,
        async (config, ffService, auth, accountService, logger) => {
            await auth.authenticated$.toPromise();
            await ffService.ready$.toPromise();

            try {
                const analyticsEnabled = await ffService.isEnabled(Feature.Analytics);
                const analyticsService = analyticsEnabled
                    ? new AnalyticsService(config, logger, ffService)
                    : NullAnalyticsService;

                const user = await accountService.getCurrentUser();

                await analyticsService.init();
                analyticsService.identify(user);

                return analyticsService;
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to instantiate AnalyticsService');
                throw e;
            }
        },
        NullAnalyticsService
    );

    await DiContainer.init(di);

    // TODO: consolidate auth service usage
    const channelAuthService = di.get(ChannelAuthenticationService);
    const authService = di.get(AuthenticationService);
    const logoutManager = di.get(LogoutManager);
    const http = di.get(TmHttpClient);
    const navService = di.get(NavigationService);
    const configService = di.get(ConfigurationService);
    const entitlementsService = di.get(EntitlementsService);
    const statusService = di.get(StatusService);
    const offlineDialog = di.get(OfflineDialogService);
    const loggingService = di.get(LoggingService);
    const applicationAreaService = di.get(ApplicationAreaService);
    const accountService = di.get(AccountService);
    const achService = di.get(AchService);
    const footerService = di.get(TmFooterService);
    const themeService = di.get(ThemeService);
    const coBrowseService = di.get(CoBrowseService);

    // set debug state out-of-band so logging service can be consumed synchronously
    di.getAsync(FeatureFlagService)
        .then(ffService =>
            Promise.all([di.getAsync(LoggingService), ffService.isEnabled(Feature.DebugMode)])
        )
        .then(([logger, isDebugEnabled]) => {
            logger.isDebugEnabled = isDebugEnabled;
        });

    themeService.init();
    configureBranding(configService.institutionId);

    // hide footer on navigation so views without footers don't have to manually hide previous footer
    navService.routeChange$.subscribe(() => footerService.hide());

    channelAuthService.authEvent$.subscribe(event => {
        switch (event) {
            case AuthFlowEvent.TermsAndConditionsAccept:
                navService.navigate('/terms-and-conditions-accept');
                break;
            case AuthFlowEvent.Done:
                navService.navigate('/dashboard');
                break;
            default:
                break;
        }
    });

    statusService.online$.subscribe(async isOnline => {
        if (isOnline) {
            offlineDialog.close();
        } else {
            offlineDialog.open();
        }
    });

    logoutManager.logout$.subscribe(() => onSessionEnd(di));
    http.sessionExpired$.subscribe(() => onSessionEnd(di));
    http.error$.subscribe(err => {
        loggingService.logError('Fetch error', err);
    });
    navService.setOutlet(AppComponent.getOutlet);

    authService.authenticated$.subscribe(() =>
        onAuthenticated(
            achService,
            configService,
            http,
            entitlementsService,
            accountService,
            loggingService,
            logoutManager,
            applicationAreaService,
            coBrowseService,
            worker
        )
    );
}

async function onAuthenticated(
    achService: AchService,
    config: ConfigurationService,
    http: TmHttpClient,
    entitlementsService: EntitlementsService,
    accountService: AccountService,
    loggingService: LoggingService,
    logoutManager: LogoutManager,
    aaService: ApplicationAreaService,
    coBrowseService: CoBrowseService,
    worker?: ServiceWorkerRegistration
) {
    if (worker) {
        const { apiRoot } = config;
        const { authToken } = http;
        worker.active?.postMessage({
            authToken,
            apiRoot,
        });
    }

    await entitlementsService.init();
    const user = await accountService.getCurrentUser();
    loggingService.setUserSessionContext(user);
    logoutManager.startLogoutTimers();
    aaService.init();
    startAlarmClock(achService);

    const di = await DiContainer.getInstance();
    di.getAsync(AnalyticsService).then(analytics => {
        analytics.track(AnalyticsEvent.LogIn);
    });

    const ffService = await di.getAsync(FeatureFlagService);
    initFfService(ffService, accountService, config.institutionId).then(async () => {
        const coBrowseEnabled = await ffService.isEnabled(Feature.CoBrowse);
        if (coBrowseEnabled) {
            coBrowseService.init();
        }
    });
}

async function onSessionEnd(di: DiContainer) {
    const dialogService = di.get(OmegaDialogService);
    dialogService.closeAll();

    const applicationAreaService = di.get(ApplicationAreaService);
    applicationAreaService.reset();

    const httpClient = di.get(TmHttpClient);
    if (httpClient instanceof JhHttpClientCached) {
        httpClient.clearCache();
    }

    const entitlementsService = di.get(EntitlementsService);
    entitlementsService.reset();

    const ffService = await di.getAsync(FeatureFlagService);
    ffService.reset();

    const analytics = await di.getAsync(AnalyticsService);
    analytics.track(AnalyticsEvent.LogOut);
    analytics.reset();

    const coBrowseService = di.get(CoBrowseService);
    coBrowseService.stop();

    const navService = di.get(NavigationService);
    navService.navigate('login');
}

async function initFfService(
    ffService: FeatureFlagService,
    accountService: AccountService,
    institutionId: string
) {
    const { loginId, isAdmin, isSuperUser, companyId } = await accountService.getCurrentUser();

    await ffService.init(institutionId, {
        userId: loginId,
        companyId,
        isAdmin,
        isSuperUser,
    });
}
